import React, { useEffect } from "react";
import {
  BrowserRouter,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";

import ApiAccess from "features/Organization/ApiAccess";
import BackendDetails from "features/Connections/BackendDetails";
import BackendList from "features/Connections/BackendList";
import Billing from "features/Billing/Billing";
import CheckUp from "features/Checks/CheckUp";
import CheckUpDetails from "features/Checks/CheckUpDetails";
import CollectorSettings from "features/Server/CollectorSettings";
import Dashboard from "features/Dashboard/Dashboard";
import IndexAdvisorServerOverview from "features/IndexAdvisor/IndexAdvisorServerOverview";
import IndexAdvisorOverview from "features/IndexAdvisor/IndexAdvisorOverview";
import LogInsights from "features/Logs/LogInsights";
import MemberDetails from "features/Organization/MemberDetails";
import MemberEdit from "features/Organization/MemberEdit";
import MemberList from "features/Organization/MemberList";
import MemberNew from "features/Organization/MemberNew";
import MemberAcceptInvite from "features/Organization/MemberAcceptInvite";
import RoleDetails from "features/Organization/RoleDetails";
import RoleEdit from "features/Organization/RoleEdit";
import RoleList from "features/Organization/RoleList";
import RoleNew from "features/Organization/RoleNew";
import SchemaFunctionDetails from "features/Schema/SchemaFunctionDetails";
import SchemaFunctionList from "features/Schema/SchemaFunctionList";
import SchemaIndexDetails from "features/Schema/SchemaIndexDetails";
import SchemaIndexList from "features/Schema/SchemaIndexList";
import SchemaTableDetails from "features/Schema/SchemaTableDetails";
import SchemaTableList from "features/Schema/SchemaTableList";
import SchemaViewList from "features/Schema/SchemaViewList";
import SchemaViewDetails from "features/Schema/SchemaViewDetails";
import ServerConnections from "features/Connections/ServerConnections";
import ServerDashboard from "features/Dashboard/ServerDashboard";
import ServerGroupDashboard from "features/Dashboard/ServerGroupDashboard";
import ServerGroupNew from "features/Server/ServerGroupNew";
import ServerGroupSettings from "features/Server/ServerGroupSettings";
import ServerGroupSettingsList from "features/Server/ServerGroupSettingsList";
import ServerLocalCollectorNew from "features/Server/ServerLocalCollectorNew";
import ServerQueryOverview from "features/Queries/ServerQueryOverview";
import ServerSchemaOverview from "features/Schema/ServerSchemaOverview";
import ServerSettings from "features/Server/ServerSettings";
import ServerSetup from "features/Setup/ServerSetup";
import SnapshotDetails from "features/Server/SnapshotDetails";
import SnapshotList from "features/Server/SnapshotList";
import SystemDetails from "features/System/SystemDetails";
import NewOrganization from "features/Organization/NewOrganization";
import OrganizationOverview from "features/Organization/OrganizationOverview";
import OrganizationSettings from "features/Organization/OrganizationSettings";
import PostgresRoleDetails from "features/System/PostgresRoleDetails";
import PostgresSettingDetails from "features/System/PostgresSettingDetails";
import PostgresSettingList from "features/System/PostgresSettingList";
import QueryDetails from "features/Queries/QueryDetails";
import QueryDetailsRoute from "features/Queries/QueryDetails/QueryDetailsRoute";
import WithQueryOrganization from "features/Queries/QueryDetails/WithQueryOrganization";
import VacuumDetails from "features/Vacuum/VacuumDetails";
import ShowFlash from "ui/ShowFlash";

import ServerInstallDocs from "features/Setup/ServerInstallDocs";

import PageLayout from "frame/PageLayout";
import RootRedirect from "frame/RootRedirect";
import { WithDateRange } from "providers/WithDateRange";
import { WithRouteState } from "utils/routeState";
import { WithHelpOpenState } from "ui/Help";
import OrganizationSetup from "features/Setup/OrganizationSetup";
import ServerSettingsList from "features/Server/ServerSettingsList";
import ServerSetupLogInsightsDocs from "features/Setup/ServerSetupLogInsightsDocs";
import ServerSetupAutomatedExplainDocs from "features/Setup/ServerSetupAutomatedExplainDocs";
import IssueDetailPage from "features/Checks/IssueDetailPage";
import OrganizationIntegrations from "features/Organization/OrganizationIntegrations";
import WithAppConfig from "providers/WithAppConfig";
import WithCurrentOrganization, {
  useCurrentServer,
  WithCurrentServerGroup,
} from "providers/WithCurrentOrganization";
import WithCurrentServer from "providers/WithCurrentServer";
import WithSubscriptionCheck from "providers/WithSubscriptionCheck";
import WithSentry from "providers/WithSentry";
import WithFlashes, { Flash, FlashLevel } from "providers/WithFlashes";
import VacuumAdvisor from "features/Vacuum/VacuumAdvisor";
import QueryPerformance from "features/Queries/QueryPerformance";
import Workbook from "features/Workbooks/WorkbookList/Workbook";
import ExplainOverview from "features/Explain/ExplainOverview";
import WithLegacyStyles from "providers/WithLegacyStyles";
import QueryTuning from "features/Workbooks/QueryTuning";
import WorkbookVariant from "features/Workbooks/WorkbookList/WorkbookVariant";
import WorkbookResult from "features/Workbooks/WorkbookList/WorkbookResult";
import ReviewQuery from "features/Workbooks/WorkbookList/ReviewQuery";
import RewriteQuery from "features/Workbooks/WorkbookList/RewriteQuery";
import Changelog from "features/Changelog/Changelog";
import QueryAdvisor from "features/QueryAdvisor/QueryAdvisor";
import QueryAdvisorServerOverview from "features/QueryAdvisor/QueryAdvisorServerOverview";
import WithServerRetention from "providers/WithServerRetention";
import ServerGroupQueryOverview from "features/Queries/ServerGroupQueryOverview";

type LegacyContentType = React.ReactNode;

type Props = {
  legacyContent?: LegacyContentType;
};

const Page: React.FunctionComponent<
  Props & {
    flashes?: Array<{
      name: string;
      msg: string;
      persist?: boolean;
    }>;
  }
> = ({ flashes, ...otherProps }) => {
  const serverFlashes: Flash[] =
    flashes?.map((serverFlash) => {
      return {
        level: serverFlash.name as FlashLevel,
        escapedHtmlMsg: serverFlash.msg,
        persist: serverFlash.persist,
      };
    }) ?? [];
  return (
    <WithAppConfig>
      <BrowserRouter useTransitions={false}>
        <WithFlashes initialFlashes={serverFlashes}>
          <WithCurrentOrganization>
            <WithCurrentServer>
              <WithSentry>
                <WithRouteState>
                  <WithServerRetention>
                    <WithDateRange>
                      <WithHelpOpenState>
                        <WithLegacyStyles>
                          <RoutePage {...otherProps} />
                        </WithLegacyStyles>
                      </WithHelpOpenState>
                    </WithDateRange>
                  </WithServerRetention>
                </WithRouteState>
              </WithSentry>
            </WithCurrentServer>
          </WithCurrentOrganization>
        </WithFlashes>
      </BrowserRouter>
    </WithAppConfig>
  );
};

export const RoutePage: React.FunctionComponent<Props> = ({
  legacyContent,
}) => (
  <PageLayout content={<RoutePageContent legacyContent={legacyContent} />} />
);

const RoutePageContent: React.FunctionComponent<{
  legacyContent: LegacyContentType;
}> = ({ legacyContent }) => {
  useStripTrailingSlashRedirect();
  const server = useCurrentServer(true);
  const schemaProcessingDisabled = server?.schemaProcessingDisabled;
  return (
    <>
      {schemaProcessingDisabled && <SchemaProcessingDisabledFlash />}

      <Routes>
        <Route index element={<RootRedirect />} />
        <Route path="/changelog" element={<Changelog />} />
        <Route path="/organizations" element={<RootRedirect />} />
        <Route path="/servers" element={<RootRedirect />} />
        <Route path="/databases" element={<RootRedirect />} />
        <Route path="/organizations/new" element={<NewOrganization />} />
        <Route path="/organizations/:slug">
          <Route index element={<OrganizationOverview />} />
          <Route path="edit" element={<OrganizationSettings />} />
          <Route path="integrations" element={<OrganizationIntegrations />} />
          <Route
            path="integrations/webhooks/:webhookEndpointId"
            element={<OrganizationIntegrations />}
          />
          <Route
            path="integrations/incident-io/:incidentIoEndpointId"
            element={<OrganizationIntegrations />}
          />
          <Route path="api" element={<ApiAccess />} />
          <Route path="members" element={<MemberList />} />
          <Route path="members/new" element={<MemberNew />} />
          <Route path="members/:id" element={<MemberDetails />} />
          <Route path="members/:id/edit" element={<MemberEdit />} />
          <Route
            path="members/accept/:inviteToken"
            element={<MemberAcceptInvite />}
          />
          <Route path="roles" element={<RoleList />} />
          <Route path="roles/new" element={<RoleNew />} />
          <Route path="roles/:id" element={<RoleDetails />} />
          <Route path="roles/:id/edit" element={<RoleEdit />} />
          <Route path="setup" element={<OrganizationSetup />} />
          <Route path="subscription" element={<Billing tab="overview" />} />
          <Route
            path="subscription/compare"
            element={<Billing tab="compare" />}
          />
          <Route
            path="subscription/activate/:planId"
            element={<Billing tab="activate_plan" />}
          />
          <Route
            path="subscription/update_billing_details"
            element={<Billing tab="update_billing_details" />}
          />
          <Route
            path="subscription/update_credit_card"
            element={<Billing tab="update_credit_card" />}
          />
          <Route
            path="subscription/cancel"
            element={<Billing tab="cancel_plan" />}
          />
          <Route element={<WithSubscriptionCheck />}>
            <Route
              path="servergroups"
              element={<OrganizationOverview tab="servergroups" />}
            ></Route>
            <Route
              path="servergroups/settings/new"
              element={<ServerGroupNew />}
            />
            <Route
              path="servergroups/settings"
              element={<ServerGroupSettingsList />}
            />
            <Route
              path="servers"
              element={<OrganizationOverview tab="servers" />}
            />
            <Route
              path="databases"
              element={<OrganizationOverview tab="databases" />}
            />
            <Route path="servers/new" element={<ServerLocalCollectorNew />} />
            <Route path="servers/settings" element={<ServerSettingsList />} />
            <Route path="servers/*" element={<ServerInstallDocs />} />
            <Route path="workbooks" element={<QueryTuning />} />
          </Route>
          <Route element={<WithSubscriptionCheck requireFeature="checkUp" />}>
            <Route path="alerts" element={<CheckUp tab="alerts" />} />
            <Route
              path="alert-destinations"
              element={<CheckUp tab="destinations" />}
            />
            <Route
              path="alert-destinations/:destinationId"
              element={<CheckUp tab="destinations" />}
            />
            <Route path="alert-rules" element={<CheckUp tab="rules" />} />
            <Route
              path="alert-rules/:alertRuleId"
              element={<CheckUp tab="rules" />}
            />
            <Route path="checks" element={<CheckUp tab="checks" />} />
            <Route path="checks/:checkGroup/:checkName">
              <Route index element={<CheckUpDetails tab="triggered" />} />
              <Route
                path="acknowledged"
                element={<CheckUpDetails tab="acknowledged" />}
              />
              <Route
                path="resolved"
                element={<CheckUpDetails tab="resolved" />}
              />
              <Route
                path="configure"
                element={<CheckUpDetails tab="configure" />}
              />
            </Route>
          </Route>
        </Route>
        <Route path="/servers/:serverId">
          <Route element={<WithSubscriptionCheck />}>
            <Route index element={<ServerDashboard />} />
            <Route path="connections" element={<ServerConnections />} />
            <Route
              path="indexadvisor"
              element={<IndexAdvisorServerOverview />}
            />
            <Route path="roles" element={<SystemDetails tab="roles" />} />
            <Route path="roles/:id" element={<PostgresRoleDetails />} />
            <Route
              path="replication"
              element={<SystemDetails tab="replication" />}
            />
            <Route path="setup" element={<ServerSetup />} />
            <Route
              path="setup/log-insights/*"
              element={<ServerSetupLogInsightsDocs />}
            />
            <Route
              path="setup/explain/*"
              element={<ServerSetupAutomatedExplainDocs />}
            />
            <Route path="edit" element={<ServerSettings />} />
            <Route path="servergroups" element={<ServerGroupSettingsList />} />
            <Route path="collector_settings" element={<CollectorSettings />} />
            <Route path="snapshots" element={<SnapshotList />} />
            <Route
              path="snapshots/:collectedAt"
              element={<SnapshotDetails />}
            />
            <Route path="queries" element={<ServerQueryOverview />} />
            <Route path="queries/explains" element={<ExplainOverview />} />
            <Route
              element={<WithSubscriptionCheck requireFeature="queryAdvisor" />}
            >
              <Route
                path="queries/advisor"
                element={<QueryAdvisorServerOverview />}
              />
            </Route>
            <Route path="workbooks" element={<QueryTuning />} />
            <Route path="queries/search" element={<ServerQueryOverview />} />
            <Route path="config" element={<PostgresSettingList />} />
            <Route
              path="config/:settingName"
              element={<PostgresSettingDetails />}
            />
            <Route path="system" element={<SystemDetails tab="overview" />} />
            <Route path="system/cpu" element={<SystemDetails tab="cpu" />} />
            <Route
              path="system/memory"
              element={<SystemDetails tab="memory" />}
            />
            <Route
              path="system/storage"
              element={<SystemDetails tab="storage" />}
            />
            <Route
              path="system/network"
              element={<SystemDetails tab="network" />}
            />
          </Route>
          <Route element={<WithSubscriptionCheck requireFeature="logs" />}>
            <Route path="logs" element={<LogInsights />} />
            <Route path="logs/:classification" element={<LogInsights />} />
          </Route>
          <Route
            element={<WithSubscriptionCheck requireFeature="schemaStats" />}
          >
            <Route path="schema" element={<ServerSchemaOverview />} />
          </Route>
          <Route element={<WithSubscriptionCheck requireFeature="checkUp" />}>
            <Route path="alerts" element={<CheckUp tab="alerts" />} />
            <Route path="alert-rules" element={<CheckUp tab="rules" />} />
            <Route
              path="alert-destinations"
              element={<CheckUp tab="destinations" />}
            />
            <Route path="checks" element={<CheckUp tab="checks" />} />
            <Route path="checks/:checkGroup/:checkName">
              <Route index element={<CheckUpDetails tab="triggered" />} />
              <Route
                path="acknowledged"
                element={<CheckUpDetails tab="acknowledged" />}
              />
              <Route
                path="resolved"
                element={<CheckUpDetails tab="resolved" />}
              />
              <Route
                path="configure"
                element={<CheckUpDetails tab="configure" />}
              />
              <Route path=":issueId" element={<IssueDetailPage />} />
            </Route>
          </Route>
          <Route
            element={
              <WithSubscriptionCheck requireFeature="activitySnapshotsVacuumAdvisor" />
            }
          >
            <Route path="vacuums" element={<VacuumAdvisor tab="overview" />} />
            <Route
              path="vacuums/bloat"
              element={<VacuumAdvisor tab="bloat" />}
            />
            <Route
              path="vacuums/freezing"
              element={<VacuumAdvisor tab="freezing" />}
            />
            <Route
              path="vacuums/performance"
              element={<VacuumAdvisor tab="performance" />}
            />
            <Route
              path="vacuums/activity"
              element={<VacuumAdvisor tab="activity" />}
            />
            <Route
              path="vacuums/:vacuumRunIdentity"
              element={<VacuumDetails />}
            />
          </Route>
        </Route>
        <Route path="/databases/:databaseId">
          <Route element={<WithSubscriptionCheck />}>
            <Route index element={<Dashboard />} />
            <Route path="backends" element={<BackendList tab="backends" />} />
            <Route
              path="wait_events"
              element={<BackendList tab="wait_events" />}
            />
            <Route
              path="indexadvisor"
              element={<IndexAdvisorOverview tab="overview" />}
            />
            <Route
              path="indexadvisor/indexing_engine"
              element={<IndexAdvisorOverview tab="indexing_engine" />}
            />
            <Route
              path="indexadvisor/index_unused"
              element={<IndexAdvisorOverview tab="index_unused" />}
            />
            <Route
              path="indexadvisor/status"
              element={<IndexAdvisorOverview tab="status" />}
            />
            <Route
              path="indexadvisor/config"
              element={<IndexAdvisorOverview tab="config" />}
            />
            <Route
              path="queries"
              element={<QueryPerformance tab="overview" />}
            />
            <Route
              path="queries/explains"
              element={<QueryPerformance tab="explains" />}
            />
            <Route path="workbooks" element={<QueryTuning />} />
            <Route path="workbooks/new" element={<ReviewQuery />} />
            <Route path="workbooks/:workbookId" element={<Workbook />} />
            <Route
              path="workbooks/:workbookId/compare"
              element={<Workbook tab="compare" />}
            />
            <Route
              path="workbooks/:workbookId/params"
              element={<Workbook tab="params" />}
            />
            <Route
              path="workbooks/:workbookId/activity"
              element={<Workbook tab="activity" />}
            />
            <Route
              path="workbooks/:workbookId/variants"
              element={<Workbook />}
            />
            <Route
              path="workbooks/:workbookId/variants/new"
              element={<RewriteQuery />}
            />
            <Route
              path="workbooks/:workbookId/variants/:variantId"
              element={<WorkbookVariant />}
            />
            <Route
              path="workbooks/:workbookId/variants/:variantId/run"
              element={<WorkbookVariant tab="run" />}
            />
            <Route
              path="workbooks/:workbookId/variants/:variantId/results/:resultId"
              element={<WorkbookResult />}
            />
            <Route
              path="queries/search"
              element={<QueryPerformance tab="search" />}
            />
            <Route
              element={<WithSubscriptionCheck requireFeature="queryAdvisor" />}
            >
              <Route path="queries/advisor">
                <Route index element={<QueryAdvisor tab="explain" />} />
                <Route
                  path="workbooks"
                  element={<QueryAdvisor tab="workbooks" />}
                />
              </Route>
            </Route>
            <Route path="queries/:queryId">
              <Route index element={<QueryDetails tab="overview" />} />
              <Route
                path="indexadvisor"
                element={<QueryDetails tab="indexadvisor" />}
              />
              <Route path="samples" element={<QueryDetails tab="samples" />} />
              <Route
                path="explains"
                element={<QueryDetails tab="explains" />}
              />
              <Route
                path="explains/:explainId"
                element={<QueryDetails tab="explains" />}
              />
              <Route path="tags" element={<QueryDetails tab="tags" />} />
              <Route path="logs" element={<QueryDetails tab="logs" />} />
            </Route>
          </Route>
          <Route element={<WithSubscriptionCheck requireFeature="logs" />}>
            <Route path="logs" element={<LogInsights />} />
            <Route path="logs/:classification" element={<LogInsights />} />
          </Route>
          <Route
            element={<WithSubscriptionCheck requireFeature="schemaStats" />}
          >
            <Route path="functions" element={<SchemaFunctionList />} />
            <Route
              path="functions/:schemaFunctionId"
              element={<SchemaFunctionDetails />}
            />
            <Route path="indexes" element={<SchemaIndexList />} />
            <Route
              path="indexes/:schemaIndexId"
              element={<SchemaIndexDetails />}
            />
            <Route path="tables" element={<SchemaTableList />} />
            <Route path="tables/:tableId">
              <Route index element={<SchemaTableDetails tab="overview" />} />
              <Route
                path="partitions"
                element={<SchemaTableDetails tab="partitions" />}
              />
              <Route
                path="queries"
                element={<SchemaTableDetails tab="queries" />}
              />
              <Route
                path="columns"
                element={<SchemaTableDetails tab="columns" />}
              />
              <Route
                path="indexes"
                element={<SchemaTableDetails tab="indexes" />}
              />
              <Route
                path="constraints"
                element={<SchemaTableDetails tab="constraints" />}
              />
              <Route
                path="vacuum"
                element={<SchemaTableDetails tab="vacuum" />}
              />
              <Route
                path="index_advisor"
                element={<SchemaTableDetails tab="index_advisor" />}
              />
              <Route
                path="vacuum_simulator"
                element={<SchemaTableDetails tab="vacuum_simulator" />}
              />
            </Route>
            <Route path="views" element={<SchemaViewList />} />
            <Route path="views/:viewId">
              <Route index element={<SchemaViewDetails tab="overview" />} />
              <Route
                path="columns"
                element={<SchemaViewDetails tab="columns" />}
              />
              <Route
                path="indexes"
                element={<SchemaViewDetails tab="indexes" />}
              />
            </Route>
          </Route>
          <Route element={<WithSubscriptionCheck requireFeature="checkUp" />}>
            <Route path="checks" element={<CheckUp tab="checks" />} />
            <Route path="checks/:checkGroup/:checkName">
              <Route index element={<CheckUpDetails tab="triggered" />} />
              <Route
                path="acknowledged"
                element={<CheckUpDetails tab="acknowledged" />}
              />
              <Route
                path="resolved"
                element={<CheckUpDetails tab="resolved" />}
              />
              <Route
                path="configure"
                element={<CheckUpDetails tab="configure" />}
              />
              <Route path=":issueId" element={<IssueDetailPage />} />
            </Route>
          </Route>
          <Route
            element={
              <WithSubscriptionCheck requireFeature="activitySnapshotsConnections" />
            }
          >
            <Route path="backends/:backendLocator">
              <Route index element={<BackendDetails tab="overview" />} />
              <Route
                path="queries"
                element={<BackendDetails tab="queries" />}
              />
              <Route
                path="wait_events"
                element={<BackendDetails tab="wait_events" />}
              />
              <Route path="logs" element={<BackendDetails tab="logs" />} />
            </Route>
          </Route>
          <Route
            element={
              <WithSubscriptionCheck requireFeature="activitySnapshotsVacuumAdvisor" />
            }
          >
            <Route
              path="vacuums/bloat"
              element={<VacuumAdvisor tab="bloat" />}
            />
            <Route
              path="vacuums/freezing"
              element={<VacuumAdvisor tab="freezing" />}
            />
            <Route
              path="vacuums/activity"
              element={<VacuumAdvisor tab="activity" />}
            />
          </Route>
        </Route>
        <Route
          path="/servergroups/:serverGroupId"
          element={<WithCurrentServerGroup />}
        >
          <Route element={<WithSubscriptionCheck />}>
            <Route
              path="servers"
              element={<ServerGroupDashboard tab="servers" />}
            />
            <Route
              path="databases"
              element={<ServerGroupDashboard tab="databases" />}
            />
            <Route path="queries" element={<ServerGroupQueryOverview />} />
            <Route path="workbooks" element={<QueryTuning />} />
            <Route path="edit" element={<ServerGroupSettings />} />
          </Route>
        </Route>
        <Route path="/queries/:fingerprint" element={<WithQueryOrganization />}>
          <Route element={<WithSubscriptionCheck />}>
            <Route index element={<QueryDetailsRoute />} />
          </Route>
        </Route>
        {/*
          For the topLevelComponents like AccountSettings and MarketplaceLink.
          These pages will not be navigated from the client, and rendered from
          the server side via its own views/*.html.erb and using renderTopLevelComponent
          to specify which component to use for rendering.
          For them, the page will be rendered like <Page legacyContent={legacyContent} />
          where the legacyContent is React.ReactNode, so passing it simply as an element.
        */}
        <Route path="*" element={legacyContent ?? "no matching route"} />
      </Routes>
    </>
  );
};

const useStripTrailingSlashRedirect = () => {
  const { pathname, search, hash } = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    if (pathname.length > 1 && pathname.endsWith("/")) {
      navigate(pathname.replace(/\/+$/, "") + search + hash, { replace: true });
    }
  }, [pathname, search, hash, navigate]);
};

const SchemaProcessingDisabledFlash: React.FunctionComponent = () => {
  return (
    <ShowFlash
      level="alert"
      msg={
        <>
          This server is over the table limit for{" "}
          <a target="_blank" href="https://pganalyze.com/pricing">
            your pganalyze plan
          </a>{" "}
          and schema information could not be processed. Many pganalyze features
          will not work correctly. You can filter the monitored tables to stay
          below the limit by using the{" "}
          <a
            target="_blank"
            href="https://pganalyze.com/docs/collector/settings#schema-filter-settings"
          >
            <code>ignore_schema_regexp</code>
          </a>{" "}
          collector setting.
        </>
      }
    />
  );
};

export default Page;
